import { StateStorage } from 'zustand/middleware';

import { logger } from '@/utils/logger/logger';

import {
  collectAllStateKeys,
  convertStateToURLEntries,
  parsePersistedZustandState,
} from './helpers/url-storage-helpers';

const TAG = 'URLLocalStorage:';

// Throttle URL updates to prevent browser flooding
let updateURLTimeout: ReturnType<typeof setTimeout> | null = null;
const URL_UPDATE_THROTTLE_MS = 100;

interface URLLocalStorageOptions {
  /**
   * Top-level persisted state keys to keep OUT of the URL sync (still persisted
   * to localStorage). Use for state whose flattened URL params would collide
   * with other params or make the URL unreadable (e.g. nested settings objects).
   */
  urlExcludeKeys?: string[];
}

/**
 * Generic localStorage storage for Zustand persist with URL sync.
 * Automatically syncs persisted state to URL params.
 */
export const createURLLocalStorage = (options: URLLocalStorageOptions = {}): StateStorage => {
  const { urlExcludeKeys = [] } = options;

  const omitExcludedKeys = (state: Record<string, unknown>): Record<string, unknown> => {
    if (urlExcludeKeys.length === 0 || !state || typeof state !== 'object') {
      return state;
    }
    return Object.fromEntries(
      Object.entries(state).filter(([key]) => !urlExcludeKeys.includes(key))
    );
  };

  return {
    getItem: (name: string): string | null => {
      try {
        return localStorage.getItem(name);
      } catch (error) {
        logger.error(TAG, 'Error reading from localStorage', { name, error });
        return null;
      }
    },

    setItem: (name: string, value: string): void => {
      try {
        // Get previous state to detect removed keys
        const previousValue = localStorage.getItem(name);

        // Skip if value hasn't changed (prevents unnecessary URL updates)
        if (previousValue === value) {
          return;
        }

        const previousState = previousValue ? parsePersistedZustandState(previousValue) : null;
        const previousKeys = previousState
          ? collectAllStateKeys(omitExcludedKeys(previousState))
          : [];

        // Save new state
        localStorage.setItem(name, value);

        // Convert new state to URL params
        const state = parsePersistedZustandState(value);
        const urlEntries = convertStateToURLEntries(omitExcludedKeys(state));
        const newKeys = Object.keys(urlEntries);

        // Find keys that were removed (existed before but not in new state)
        const removedKeys = previousKeys.filter(key => !newKeys.includes(key));

        // Set removed keys to null so they get deleted from URL
        removedKeys.forEach(key => {
          urlEntries[key] = null;
        });

        urlParamUtils.setMultiple(urlEntries);
      } catch (error) {
        logger.error(TAG, 'Error writing to localStorage', { name, error });
      }
    },

    removeItem: (name: string): void => {
      try {
        const value = localStorage.getItem(name);
        localStorage.removeItem(name);

        if (value) {
          const state = parsePersistedZustandState(value);
          const keysToDelete = collectAllStateKeys(omitExcludedKeys(state));
          urlParamUtils.deleteMultiple(keysToDelete);
        }
      } catch (error) {
        logger.error(TAG, 'Error removing from localStorage', { name, error });
      }
    },
  };
};

// ============================================================================
// URL Parameter Utilities
// ============================================================================

/**
 * Generic URL search params utilities.
 * Provides functions to read/write URL params.
 */
export const urlParamUtils = {
  /**
   * Get a single URL param value
   */
  get(key: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  },

  /**
   * Get all URL params
   */
  getAll(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  },

  /**
   * Set multiple URL params at once
   */
  setMultiple(entries: Record<string, string | null>): void {
    try {
      const params = new URLSearchParams(window.location.search);

      Object.entries(entries).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      this.updateURL(params);
    } catch (error) {
      logger.error(TAG, 'Error setting multiple URL params', { error });
    }
  },

  /**
   * Delete multiple URL params at once
   */
  deleteMultiple(keys: string[]): void {
    try {
      const params = new URLSearchParams(window.location.search);
      keys.forEach(key => params.delete(key));
      this.updateURL(params);
    } catch (error) {
      logger.error(TAG, 'Error deleting multiple URL params', { keys, error });
    }
  },

  /**
   * Update the URL without page reload (throttled to prevent browser flooding)
   */
  updateURL(params: URLSearchParams): void {
    // Clear existing timeout
    if (updateURLTimeout) {
      clearTimeout(updateURLTimeout);
    }

    // Throttle URL updates to prevent "Throttling navigation" browser error
    // But execute immediately if we're deleting params (filter clear scenario)
    const currentParams = new URLSearchParams(window.location.search);
    const isDeleting = currentParams.toString().length > params.toString().length;

    const doUpdate = () => {
      const url = new URL(window.location.href);
      url.search = params.toString();
      window.history.replaceState({}, '', url);
      updateURLTimeout = null;
    };

    if (isDeleting) {
      // Execute immediately for deletions (like clearing filters)
      doUpdate();
    } else {
      // Throttle for additions/updates
      updateURLTimeout = setTimeout(doUpdate, URL_UPDATE_THROTTLE_MS);
    }
  },
};
